import type { TelemetrySessionContext } from '@kangaroo/shared';

const DISMISSED_KEY_PREFIX = 'paddiq:circuit-discovery-dismissed';
const RESOLUTIONS_KEY_PREFIX = 'paddiq:circuit-match-resolutions';
const LEGACY_DISMISSED_KEY = 'paddiq:circuit-discovery-dismissed';

export type CircuitMatchCacheStatus = 'matched' | 'unmatched';

export type CircuitMatchCacheEntry = {
  sessionKey: string;
  status: CircuitMatchCacheStatus;
  contributionId: string | null;
  trackId: string | null;
  circuitName: string | null;
  fileUpdatedAt: string;
  resolvedAt: number;
};

let cacheScopeId: string | null = null;

type DismissedDiscoveryStore = {
  sessions: string[];
  venues: string[];
  locations: string[];
};

const memoryDismissed = new Set<string>();
const memoryDismissedVenues = new Set<string>();
const memoryDismissedLocations = new Set<string>();
const memoryResolutions = new Map<string, CircuitMatchCacheEntry>();
let dismissedLoaded = false;
let resolutionsLoaded = false;

export function setCircuitDiscoveryCacheScope(scopeId: string | null) {
  cacheScopeId = scopeId;
  memoryDismissed.clear();
  memoryDismissedVenues.clear();
  memoryDismissedLocations.clear();
  memoryResolutions.clear();
  dismissedLoaded = false;
  resolutionsLoaded = false;
}

function dismissedStorageKey() {
  return cacheScopeId ? `${DISMISSED_KEY_PREFIX}:${cacheScopeId}` : DISMISSED_KEY_PREFIX;
}

function resolutionsStorageKey() {
  return cacheScopeId ? `${RESOLUTIONS_KEY_PREFIX}:${cacheScopeId}` : RESOLUTIONS_KEY_PREFIX;
}

function hasBrowserStorage() {
  return typeof localStorage !== 'undefined';
}

function parseDismissedDiscoveryStore(raw: string): DismissedDiscoveryStore {
  const parsed = JSON.parse(raw) as unknown;

  if (Array.isArray(parsed)) {
    return {
      sessions: parsed.filter((entry): entry is string => typeof entry === 'string'),
      venues: [],
      locations: [],
    };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { sessions: [], venues: [], locations: [] };
  }

  const store = parsed as Partial<DismissedDiscoveryStore>;
  return {
    sessions: Array.isArray(store.sessions)
      ? store.sessions.filter((entry): entry is string => typeof entry === 'string')
      : [],
    venues: Array.isArray(store.venues)
      ? store.venues.filter((entry): entry is string => typeof entry === 'string')
      : [],
    locations: Array.isArray(store.locations)
      ? store.locations.filter((entry): entry is string => typeof entry === 'string')
      : [],
  };
}

function readDismissedFromStorage(): DismissedDiscoveryStore {
  if (!hasBrowserStorage()) {
    return { sessions: [], venues: [], locations: [] };
  }

  try {
    const scopedRaw = localStorage.getItem(dismissedStorageKey());
    const raw = scopedRaw ?? localStorage.getItem(LEGACY_DISMISSED_KEY);
    if (!raw) {
      return { sessions: [], venues: [], locations: [] };
    }

    const store = parseDismissedDiscoveryStore(raw);

    if (!scopedRaw && cacheScopeId && (store.sessions.length > 0 || store.venues.length > 0 || store.locations.length > 0)) {
      localStorage.setItem(dismissedStorageKey(), JSON.stringify(store));
      localStorage.removeItem(LEGACY_DISMISSED_KEY);
    }

    return store;
  } catch {
    return { sessions: [], venues: [], locations: [] };
  }
}

function writeDismissedToStorage() {
  if (!hasBrowserStorage()) {
    return;
  }

  localStorage.setItem(
    dismissedStorageKey(),
    JSON.stringify({
      sessions: [...memoryDismissed],
      venues: [...memoryDismissedVenues],
      locations: [...memoryDismissedLocations],
    }),
  );
}

function readResolutionsFromStorage(): Map<string, CircuitMatchCacheEntry> {
  if (!hasBrowserStorage()) {
    return new Map();
  }

  try {
    const raw = localStorage.getItem(resolutionsStorageKey());
    if (!raw) {
      return new Map();
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return new Map();
    }

    const entries = new Map<string, CircuitMatchCacheEntry>();
    for (const [sessionKey, value] of Object.entries(parsed)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        continue;
      }

      const entry = value as Partial<CircuitMatchCacheEntry>;
      if (
        entry.sessionKey !== sessionKey
        || (entry.status !== 'matched' && entry.status !== 'unmatched')
        || typeof entry.fileUpdatedAt !== 'string'
        || typeof entry.resolvedAt !== 'number'
      ) {
        continue;
      }

      entries.set(sessionKey, {
        sessionKey,
        status: entry.status,
        contributionId: entry.contributionId ?? null,
        trackId: entry.trackId ?? null,
        circuitName: entry.circuitName ?? null,
        fileUpdatedAt: entry.fileUpdatedAt,
        resolvedAt: entry.resolvedAt,
      });
    }

    return entries;
  } catch {
    return new Map();
  }
}

function writeResolutionsToStorage(entries: Map<string, CircuitMatchCacheEntry>) {
  if (!hasBrowserStorage()) {
    return;
  }

  const payload = Object.fromEntries(entries);
  localStorage.setItem(resolutionsStorageKey(), JSON.stringify(payload));
}

function ensureDismissedLoaded() {
  if (dismissedLoaded) {
    return;
  }

  dismissedLoaded = true;
  const store = readDismissedFromStorage();
  for (const key of store.sessions) {
    memoryDismissed.add(key);
  }
  for (const key of store.venues) {
    memoryDismissedVenues.add(key);
  }
  for (const key of store.locations) {
    memoryDismissedLocations.add(key);
  }
}

function ensureResolutionsLoaded() {
  if (resolutionsLoaded) {
    return;
  }

  resolutionsLoaded = true;
  for (const [sessionKey, entry] of readResolutionsFromStorage()) {
    memoryResolutions.set(sessionKey, entry);
  }
}

export function getDismissedCircuitDiscoveryKeys() {
  ensureDismissedLoaded();
  return new Set(memoryDismissed);
}

export function getDismissedCircuitDiscoveryVenueKeys() {
  ensureDismissedLoaded();
  return new Set(memoryDismissedVenues);
}

export function getDismissedCircuitDiscoveryLocationKeys() {
  ensureDismissedLoaded();
  return new Set(memoryDismissedLocations);
}

export function dismissCircuitDiscoveryKey(sessionKey: string) {
  ensureDismissedLoaded();
  memoryDismissed.add(sessionKey);
  writeDismissedToStorage();
}

export function dismissCircuitDiscoveryVenueKey(venueKey: string) {
  const normalized = venueKey.trim().toLowerCase();
  if (!normalized) {
    return;
  }

  ensureDismissedLoaded();
  memoryDismissedVenues.add(normalized);
  writeDismissedToStorage();
}

export function restoreCircuitDiscoveryVenueKey(venueKey: string) {
  const normalized = venueKey.trim().toLowerCase();
  if (!normalized) {
    return;
  }

  ensureDismissedLoaded();
  memoryDismissedVenues.delete(normalized);
  writeDismissedToStorage();
}

export function dismissCircuitDiscoveryLocationKey(locationKey: string) {
  const normalized = locationKey.trim().toLowerCase();
  if (!normalized) {
    return;
  }

  ensureDismissedLoaded();
  memoryDismissedLocations.add(normalized);
  writeDismissedToStorage();
}

export function restoreCircuitDiscoveryLocationKey(locationKey: string) {
  const normalized = locationKey.trim().toLowerCase();
  if (!normalized) {
    return;
  }

  ensureDismissedLoaded();
  memoryDismissedLocations.delete(normalized);
  writeDismissedToStorage();
}

export function restoreCircuitDiscoveryKey(sessionKey: string) {
  ensureDismissedLoaded();
  memoryDismissed.delete(sessionKey);
  writeDismissedToStorage();
}

export function getCachedCircuitMatch(
  sessionKey: string,
  fileUpdatedAt: string,
): CircuitMatchCacheEntry | null {
  ensureResolutionsLoaded();
  const entry = memoryResolutions.get(sessionKey);
  if (!entry || entry.fileUpdatedAt !== fileUpdatedAt) {
    return null;
  }

  return entry;
}

export function setCachedCircuitMatch(entry: CircuitMatchCacheEntry) {
  ensureResolutionsLoaded();
  memoryResolutions.set(entry.sessionKey, entry);
  writeResolutionsToStorage(memoryResolutions);
}

export function getCachedCircuitMatchContexts(
  teamId: string,
): Map<string, TelemetrySessionContext | null> {
  ensureResolutionsLoaded();
  const contexts = new Map<string, TelemetrySessionContext | null>();

  for (const entry of memoryResolutions.values()) {
    if (entry.status !== 'matched' || !entry.contributionId || !entry.trackId) {
      continue;
    }

    contexts.set(entry.sessionKey, {
      sessionKey: entry.sessionKey,
      teamId,
      dataFileId: entry.sessionKey,
      driverId: null,
      kartId: null,
      kartSetupHistoryEntryId: null,
      trackId: entry.trackId,
      contributionId: entry.contributionId,
      recordedAtMs: null,
      createdAt: new Date(entry.resolvedAt).toISOString(),
      updatedAt: new Date(entry.resolvedAt).toISOString(),
    });
  }

  return contexts;
}

export function clearDismissedCircuitDiscoveries(userId?: string | null) {
  memoryDismissed.clear();
  memoryDismissedVenues.clear();
  memoryDismissedLocations.clear();
  dismissedLoaded = false;

  if (!hasBrowserStorage()) {
    return;
  }

  const dismissedKey = userId ? `${DISMISSED_KEY_PREFIX}:${userId}` : dismissedStorageKey();
  localStorage.removeItem(dismissedKey);

  if (!userId) {
    localStorage.removeItem(LEGACY_DISMISSED_KEY);
  }
}

export function clearCircuitDiscoveryCache(userId?: string | null) {
  memoryDismissed.clear();
  memoryDismissedVenues.clear();
  memoryDismissedLocations.clear();
  memoryResolutions.clear();
  dismissedLoaded = false;
  resolutionsLoaded = false;

  if (!hasBrowserStorage()) {
    return;
  }

  const dismissedKey = userId ? `${DISMISSED_KEY_PREFIX}:${userId}` : dismissedStorageKey();
  const resolutionsKey = userId ? `${RESOLUTIONS_KEY_PREFIX}:${userId}` : resolutionsStorageKey();

  localStorage.removeItem(dismissedKey);
  localStorage.removeItem(resolutionsKey);

  if (!userId) {
    localStorage.removeItem(LEGACY_DISMISSED_KEY);
  }
}
